<template>
  <div class="ppm-header">

    <div class="ppm-header__row">

      <q-input
        :model-value="projectName"
        outlined dense readonly
        label="Проект УСП"
        style="width: 300px;"
      >
        <template v-slot:prepend>
          <q-btn icon="list" flat dense round size="sm" color="grey-6" @click="$emit('open-project-dialog')">
            <q-tooltip>Выбрать проект</q-tooltip>
          </q-btn>
        </template>
        <template v-slot:append>
          <q-icon
            v-if="projectGuid"
            name="close"
            size="14px"
            class="cursor-pointer ppm-clear-icon"
            @click="$emit('clear-project')"
          />
        </template>
      </q-input>

      <div class="ppm-period-wrap" @click="$refs.periodPopup.show()">
        <q-input
          :model-value="periodLabel"
          outlined dense readonly
          label="Период"
          style="width: 210px;"
        >
          <template v-slot:prepend>
            <q-btn icon="event" flat dense round size="sm" color="grey-6" @click.stop="$refs.periodPopup.show()">
              <q-tooltip>Выбрать период</q-tooltip>
            </q-btn>
          </template>
          <template v-slot:append>
            <q-icon
              v-if="hasDates"
              name="close"
              size="14px"
              class="cursor-pointer ppm-clear-icon"
              @click.stop="$emit('clear-dates')"
            />
          </template>
        </q-input>
        <q-popup-proxy ref="periodPopup" transition-show="scale" transition-hide="scale">
          <q-date
            :model-value="periodRange"
            @update:model-value="$emit('update:periodRange', $event)"
            range
            mask="DD.MM.YYYY"
            minimal
            :options="dateNotFuture"
          />
        </q-popup-proxy>
      </div>

      <q-separator vertical inset style="height: 28px;" />

      <q-select
        :model-value="filterContractor"
        @update:model-value="$emit('update:filterContractor', $event)"
        dense outlined
        label="Контрагент"
        style="width: 200px;"
        :options="contractorOptions"
        option-label="name"
        option-value="id"
        emit-value
        map-options
        :disable="!contractorOptions.length"
        no-options-label="Нет доступных значений"
      >
        <template v-slot:append>
          <q-icon
            v-if="filterContractor != null"
            name="close"
            size="14px"
            class="cursor-pointer ppm-clear-icon"
            @click.stop="$emit('update:filterContractor', null)"
          />
        </template>
      </q-select>

      <q-select
        :model-value="filterType"
        @update:model-value="$emit('update:filterType', $event)"
        dense outlined
        label="Конструктив"
        style="width: 200px;"
        :options="projectTypeOptions"
        option-label="name"
        option-value="id"
        emit-value
        map-options
        :disable="!projectTypeOptions.length"
        no-options-label="Нет доступных значений"
      >
        <template v-slot:append>
          <q-icon
            v-if="filterType != null"
            name="close"
            size="14px"
            class="cursor-pointer ppm-clear-icon"
            @click.stop="$emit('update:filterType', null)"
          />
        </template>
      </q-select>

      <q-space />
      <div class="ppm-header__actions">

      <q-toggle
        v-if="canStorno"
        :model-value="stornoMode"
        @update:model-value="$emit('update:storno-mode', $event)"
        dense
        color="deep-orange"
        label="Сторно +/−"
        class="ppm-header__storno-toggle"
        :disable="!hasTreeData"
      >
        <q-tooltip>Режим корректировки: клик по отправленной ячейке откроет сторно</q-tooltip>
      </q-toggle>

      <q-separator vertical inset style="height: 28px;" class="q-mx-xs" />

      <q-btn
        v-if="allExpanded"
        icon="unfold_less"
        label="Свернуть"
        flat dense no-caps size="sm"
        :disable="!hasTreeData || expandAllProgress.active"
        :loading="expandAllProgress.active"
        class="ppm-header__btn"
        @click="$emit('collapse-all')"
      >
        <q-tooltip>Свернуть все раскрытые узлы</q-tooltip>
      </q-btn>

      <q-btn
        icon="refresh"
        label="Обновить"
        flat dense no-caps size="sm"
        :disable="!hasTreeData || expandAllProgress.active"
        :loading="expandAllProgress.active"
        class="ppm-header__btn"
        @click="$emit('refresh-nodes')"
      >
        <q-tooltip>Полностью перезагрузить данные проекта (как при перевыборе)</q-tooltip>
      </q-btn>

      <q-btn
        icon="send"
        label="Отправить в УСП"
        unelevated dense no-caps size="sm"
        color="primary"
        class="ppm-header__btn ppm-header__btn--primary"
        :disable="!canSend"
        :loading="uspSending"
        @click="$emit('send-to-usp')"
      >
        <q-tooltip>Отправить выбранные данные в УСП</q-tooltip>
      </q-btn>

      <q-btn
        icon="code"
        label="JSON"
        flat dense no-caps size="sm"
        color="grey-7"
        class="ppm-header__btn"
        @click="$emit('open-json')"
      >
        <q-tooltip>Данные таблицы в формате JSON</q-tooltip>
      </q-btn>

      <q-btn
        icon="badge"
        label="Названия"
        :color="showFullNames ? 'indigo-7' : 'grey-6'"
        :outline="showFullNames"
        flat dense no-caps size="sm"
        class="ppm-header__btn"
        @click="$emit('toggle-show-full-names')"
      >
        <q-tooltip>{{ showFullNames ? 'Короткие названия' : 'Полные названия (ID / конструктив / работа)' }}</q-tooltip>
      </q-btn>

      <q-btn
        icon="assessment"
        label="Отчёт"
        flat dense no-caps size="sm"
        color="primary"
        class="ppm-header__btn"
        :disable="!projectGuid"
        @click="$emit('open-usp-report')"
      >
        <q-tooltip>Отчёт УСП (отправленное / данные УСП)</q-tooltip>
      </q-btn>

      <q-btn
        icon="compare_arrows"
        label="Расхождения"
        flat dense no-caps size="sm"
        color="deep-orange"
        class="ppm-header__btn"
        :disable="!projectGuid"
        @click="$emit('open-discrepancy')"
      >
        <q-tooltip>Отчёт по расхождениям с УСП</q-tooltip>
      </q-btn>

      <q-btn
        v-if="canStornoList"
        icon="undo"
        label="Сторнирование"
        flat dense no-caps size="sm"
        color="indigo-7"
        class="ppm-header__btn"
        :disable="!projectGuid"
        @click="$emit('open-storno-list')"
      >
        <q-tooltip>Плоский список работ для сторнирования</q-tooltip>
      </q-btn>

      <q-btn
        :icon="isFullscreen ? 'fullscreen_exit' : 'fullscreen'"
        :label="isFullscreen ? 'Свернуть' : 'Полный экран'"
        flat dense no-caps size="sm"
        color="grey-6"
        class="ppm-header__btn"
        @click="$emit('toggle-fullscreen')"
      >
        <q-tooltip>{{ isFullscreen ? 'Выйти из полноэкранного режима' : 'Развернуть на весь экран' }}</q-tooltip>
      </q-btn>
      </div>
    </div>

    <q-linear-progress v-if="isLoading" indeterminate />

    <div v-if="expandAllProgress.active" class="expand-all-progress">
      <q-icon name="sync" size="14px" color="indigo-6" class="expand-all-progress__icon" />
      <span class="expand-all-progress__text">
        Загрузка работ: {{ expandAllProgress.done }} / {{ expandAllProgress.total }}
      </span>
      <q-linear-progress
        :value="expandAllProgress.total ? expandAllProgress.done / expandAllProgress.total : 0"
        color="indigo-6"
        track-color="indigo-1"
        rounded
        style="flex:1; height:6px;"
      />
    </div>
  </div>
</template>

<script>
export default {
  name: 'PpmHeader',
  props: {
    projectGuid: String,
    projectName: String,
    periodLabel: String,
    periodRange: [Object, String],
    filterContractor: [String, Number, null],
    filterType: [String, Number, null],
    contractorOptions: { type: Array, default: () => [] },
    projectTypeOptions: { type: Array, default: () => [] },
    isLoading: Boolean,
    allExpanded: Boolean,
    expandAllProgress: { type: Object, default: () => ({ active: false, done: 0, total: 0 }) },
    canSend: Boolean,
    uspSending: Boolean,
    showFullNames: Boolean,
    isFullscreen: Boolean,
    hasTreeData: Boolean,
    hasDates: Boolean,
    dateNotFuture: Function,
    isFutureDate: Boolean,
    stornoMode: { type: Boolean, default: false },
    canStorno: { type: Boolean, default: false },
    canStornoList: { type: Boolean, default: false },
  },
  emits: [
    'open-project-dialog', 'clear-project', 'clear-dates',
    'collapse-all', 'refresh-nodes', 'send-to-usp', 'open-json',
    'toggle-show-full-names', 'open-usp-report', 'open-discrepancy', 'open-storno-list',
    'toggle-fullscreen',
    'update:periodRange', 'update:filterContractor', 'update:filterType',
    'update:storno-mode',
  ],
};
</script>
